// Constants
const TIMEOUT_DELAYS = {
  DEBOUNCE_SUBMIT: 500,
  UPDATE_STICKY_ELEMENTS: 50,
  VISIBILITY_UPDATE: 10,
  AJAX_UPDATE: 200,
  SCROLL_RESTORE: 0,
};

const DESKTOP_BREAKPOINT = 990;
const SCROLL_THRESHOLD = 100;

// Selectors
const SELECTORS = {
  MOBILE_FILTER_DETAILS: ".mobile-facets__details.js-filter",
  MOBILE_CHECKBOX: ".mobile-facets__checkbox",
  MOBILE_SUMMARY: ".mobile-facets__summary",
  SECTION_BADGE: ".mobile-facets__badge--section",
  PRICE_RANGE: "price-range",
  ACTIVE_FACETS_DESKTOP: ".active-facets-desktop facet-remove",
  ACTIVE_FACETS_BUTTON: ".active-facets__button-remove",
  SORT_SELECTS: "#SortBy, #SortBy-mobile, #SortBy-mobile-sticky",
  SORT_TEXT_ELEMENTS: ".facets-mobile-sort-value, .facets-sticky-sort-value",
  MOBILE_FORMS: "#FacetFiltersFormMobile, #FacetFiltersFormMobileSticky",
};

// Form IDs
const FORM_IDS = {
  MOBILE: "FacetFiltersFormMobile",
  MOBILE_STICKY: "FacetFiltersFormMobileSticky",
  DESKTOP: "FacetFiltersForm",
  SORT: "FacetSortForm",
  SORT_DRAWER: "FacetSortDrawerForm",
};

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, TIMEOUT_DELAYS.DEBOUNCE_SUBMIT);

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  // Helper: Get cached DOM elements
  static getCountContainers() {
    if (!this._countContainers) {
      this._countContainers = {
        mobile: document.getElementById("ProductCount"),
        desktop: document.getElementById("ProductCountDesktop"),
      };
    }
    return this._countContainers;
  }

  // Helper: Remove loading states from count containers
  static removeLoadingStates() {
    const containers = this.getCountContainers();
    if (containers.mobile) containers.mobile.classList.remove("loading");
    if (containers.desktop) containers.desktop.classList.remove("loading");

    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner",
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
  }

  // Helper: Bind drawer events
  static bindDrawerEvents(formId) {
    const form = document.getElementById(formId);
    if (!form) return;

    const drawer = form.closest("menu-drawer");
    if (drawer && typeof drawer.bindEvents === "function") {
      drawer.bindEvents();
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const containers = this.getCountContainers();
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner",
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove("hidden"));

    const productGridContainer = document.getElementById(
      "ProductGridContainer",
    );
    if (productGridContainer) {
      const collection = productGridContainer.querySelector(".collection");
      if (collection) {
        collection.classList.add("loading");
      }
    }

    if (containers.mobile) containers.mobile.classList.add("loading");
    if (containers.desktop) containers.desktop.classList.add("loading");

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === "function")
          initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === "function")
      initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById("ProductGridContainer");
    if (!container) {
      console.warn("ProductGridContainer not found");
      return;
    }

    try {
      const parsedHTML = new DOMParser().parseFromString(html, "text/html");
      const parsedContainer = parsedHTML.getElementById("ProductGridContainer");

      if (!parsedContainer) {
        console.warn("ProductGridContainer not found in parsed HTML");
        return;
      }

      container.innerHTML = parsedContainer.innerHTML;

      // Remove loading class and overlay after content is rendered
      const collection = container.querySelector(".collection");
      if (collection) {
        collection.classList.remove("loading");
        const loadingOverlay = collection.querySelector(".loading-overlay");
        if (loadingOverlay) {
          loadingOverlay.remove();
        }
      }

      // Cancel scroll triggers
      container.querySelectorAll(".scroll-trigger").forEach((element) => {
        element.classList.add("scroll-trigger--cancel");
      });
    } catch (error) {
      console.error("Error rendering product grid container:", error);
    }
  }

  static renderProductCount(html) {
    try {
      const parsedHTML = new DOMParser().parseFromString(html, "text/html");

      // Try ProductCountDesktop first (it's more commonly rendered)
      let parsedCountElement = parsedHTML.getElementById("ProductCountDesktop");
      let countElementId = "ProductCountDesktop";

      // Fallback to ProductCount if ProductCountDesktop doesn't exist
      if (!parsedCountElement) {
        parsedCountElement = parsedHTML.getElementById("ProductCount");
        countElementId = "ProductCount";
      }

      // If neither exists, just remove loading states silently (ProductCount is commented out in some templates)
      if (!parsedCountElement) {
        this.removeLoadingStates();
        return;
      }

      const count = parsedCountElement.innerHTML;
      const containers = this.getCountContainers();

      if (containers.mobile) {
        containers.mobile.innerHTML = count;
        containers.mobile.classList.remove("loading");
      }

      if (containers.desktop) {
        containers.desktop.innerHTML = count;
        containers.desktop.classList.remove("loading");
      }

      const loadingSpinners = document.querySelectorAll(
        ".facets-container .loading__spinner, facet-filters-form .loading__spinner",
      );
      loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
    } catch (error) {
      console.error("Error rendering product count:", error);
      // Still try to remove loading states on error
      this.removeLoadingStates();
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const facetDetailsElements = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersFormMobileSticky .js-filter, #FacetFiltersPillsForm .js-filter",
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element),
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const targetElements = document.querySelectorAll(
        `.js-filter[data-index="${element.dataset.index}"]`,
      );
      targetElements.forEach((target) => {
        target.innerHTML = element.innerHTML;
      });
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest(".js-filter").id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          event.target.closest(".js-filter"),
        );
        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(closestJSFilterID),
        );

        const newElementSelector = document
          .getElementById(closestJSFilterID)
          .classList.contains("mobile-facets__details")
          ? `#${closestJSFilterID} .mobile-facets__close-button`
          : `#${closestJSFilterID} .facets__summary`;
        const newElementToActivate = document.querySelector(newElementSelector);
        if (newElementToActivate) newElementToActivate.focus();
      }
    }

    // Update section badges after filters are rendered
    setTimeout(() => {
      FacetStickyManager.updateSectionBadges();
    }, TIMEOUT_DELAYS.UPDATE_STICKY_ELEMENTS);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      ".active-facets-mobile",
      ".active-facets-desktop",
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const targetElement = document.querySelector(selector);
      if (targetElement) {
        targetElement.innerHTML = activeFacetsElement.innerHTML;
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
    // Update badge count after active facets are rendered
    FacetStickyManager.updateBadgeCount();
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      ".mobile-facets__open",
      ".mobile-facets__count",
      ".sorting",
    ];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const elements = document.querySelectorAll(selector);
      const sourceElement = html.querySelector(selector);
      elements.forEach((el) => {
        el.innerHTML = sourceElement.innerHTML;
      });
    });

    // Bind drawer events for both mobile forms
    this.bindDrawerEvents(FORM_IDS.MOBILE);
    this.bindDrawerEvents(FORM_IDS.MOBILE_STICKY);

    // Re-setup listeners after DOM updates
    if (FacetStickyManager._setupSortListeners) {
      FacetStickyManager._setupSortListeners();
    }
    if (FacetStickyManager._setupDrawerPrevention) {
      FacetStickyManager._setupDrawerPrevention();
    }

    // Update sticky section elements after a brief delay to ensure DOM is updated
    setTimeout(() => {
      FacetStickyManager.updateBadgeCount();
      FacetStickyManager.updateSectionBadges();
      FacetStickyManager.updateSortText();
    }, TIMEOUT_DELAYS.UPDATE_STICKY_ELEMENTS);
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector(".facets__header");
    const sourceHeaderElement = source.querySelector(".facets__header");

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector(".facets-wrap");
    const sourceWrapElement = source.querySelector(".facets-wrap");

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(
        target.querySelector("show-more-button .label-show-more.hidden"),
      );
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(".facets__item.hidden")
          .forEach((hiddenItem) =>
            hiddenItem.classList.replace("hidden", "show-more-item"),
          );
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector(".mobile-facets__list");
    const sourceFacetsList = source.querySelector(".mobile-facets__list");

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form",
    );
    const formElement = event.target.closest("form");
    const isMobileForm = this.isMobileForm(formElement);

    if (event.srcElement.className == "mobile-facets__checkbox") {
      // Update section badge immediately for better UX
      FacetStickyManager.updateSectionBadges();
      let searchParams = this.createSearchParams(formElement);
      this.onSubmitForm(searchParams, event);
    } else {
      // Update section badges for price range and other mobile inputs
      if (isMobileForm) {
        FacetStickyManager.updateSectionBadges();
      }

      const forms = [];
      const isMobile = formElement?.id === FORM_IDS.MOBILE;
      const isStickyMobile = formElement?.id === FORM_IDS.MOBILE_STICKY;

      sortFilterForms.forEach((form) => {
        if (isMobile || isStickyMobile) {
          // Only use the form that triggered the event, not both mobile forms
          if (form.id === formElement.id) {
            forms.push(this.createSearchParams(form));
          }
        } else {
          if (
            form.id === FORM_IDS.SORT ||
            form.id === FORM_IDS.DESKTOP ||
            form.id === FORM_IDS.SORT_DRAWER
          ) {
            const noJsElements = document.querySelectorAll(".no-js-list");
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        }
      });
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  isMobileForm(formElement) {
    return (
      formElement &&
      (formElement.id === FORM_IDS.MOBILE ||
        formElement.id === FORM_IDS.MOBILE_STICKY)
    );
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = this.extractSearchParams(event.currentTarget.href);
    FacetFiltersForm.renderPage(url);
  }

  extractSearchParams(url) {
    try {
      // Handle both absolute and relative URLs
      const urlObj = new URL(url, window.location.origin);
      // urlObj.search includes the '?', so remove it
      return urlObj.search.substring(1);
    } catch (e) {
      // Fallback for edge cases (e.g., malformed URLs)
      const queryIndex = url.indexOf("?");
      return queryIndex === -1 ? "" : url.slice(queryIndex + 1);
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) =>
      element.addEventListener("change", this.onRangeChange.bind(this)),
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("min", 0);
    if (maxInput.value === "")
      minInput.setAttribute("max", maxInput.getAttribute("max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);

// Sticky Filter/Sort Manager
class FacetStickyManager {
  static init() {
    this.setupScrollDetection();
    this.setupButtonPrevention();
    this.updateBadgeCount();
    this.updateSortText();
    this.setupSortChangeListener();
    this.setupDesktopDrawerPrevention();
    this.handleDesktopDrawer();
    this.setupStickyDrawerActiveClass();
    this.setupDrawerBodyClass();
  }

  static setupScrollDetection() {
    // Prevent multiple initializations
    if (this._scrollDetectionSetup) return;
    this._scrollDetectionSetup = true;

    const stickyWrapper = document.getElementById("FacetsStickyWrapper");
    const facetsContainer = document.querySelector(".facets-container");
    const mobileNonSticky = document.querySelector(".facets-mobile-non-sticky");

    if (!stickyWrapper) return;

    const threshold = SCROLL_THRESHOLD;

    const checkScroll = () => {
      // Check if mobile non-sticky is visible (not hidden by CSS)
      let referenceElement = null;
      if (mobileNonSticky) {
        const style = window.getComputedStyle(mobileNonSticky);
        if (style.display !== "none" && style.visibility !== "hidden") {
          referenceElement = mobileNonSticky;
        }
      }

      // Fallback to facets-container if mobile non-sticky is not visible
      if (!referenceElement && facetsContainer) {
        // Check if facets-container is actually visible and has height
        const containerStyle = window.getComputedStyle(facetsContainer);
        if (
          containerStyle.display !== "none" &&
          containerStyle.visibility !== "hidden"
        ) {
          referenceElement = facetsContainer;
        }
      }

      if (!referenceElement) {
        // If no reference element, show sticky on any scroll
        if (window.scrollY > threshold) {
          stickyWrapper.classList.add("visible");
        } else {
          stickyWrapper.classList.remove("visible");
        }
        return;
      }

      const elementRect = referenceElement.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;

      // Get the bottom position of the reference element relative to document
      // elementRect.top is relative to viewport, so we add scrollY to get absolute position
      const elementTop = scrollY + elementRect.top;
      const elementHeight = Math.max(elementRect.height, 0); // Ensure non-negative height
      const elementBottom = elementTop + elementHeight;

      // Show sticky when scrolled past the element bottom + threshold
      // elementBottom is the absolute position from top of document
      // scrollY is current scroll position
      // We want to show when scrollY >= elementBottom + threshold
      const thresholdPoint = elementBottom + threshold;

      if (scrollY >= thresholdPoint) {
        stickyWrapper.classList.add("visible");
      } else {
        stickyWrapper.classList.remove("visible");
      }

      // Update active class when visibility changes
      if (this._updateStickyActiveClass) {
        this._updateStickyActiveClass();
      }
    };

    // Check on load
    checkScroll();

    // Check on scroll - use single listener
    if (!this._scrollHandler) {
      let ticking = false;
      this._scrollHandler = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            checkScroll();
            ticking = false;
          });
          ticking = true;
        }
      };
      window.addEventListener("scroll", this._scrollHandler, { passive: true });
    }

    // Check on resize - use single listener
    if (!this._resizeHandler) {
      this._resizeHandler = checkScroll;
      window.addEventListener("resize", this._resizeHandler);
    }
  }

  static calculateFilterCount() {
    let count = 0;
    // Only count from active-facets-desktop to avoid double counting
    // active-facets-mobile is hidden but still in DOM with same filters, so we skip it
    const activeFacets = document.querySelectorAll(
      SELECTORS.ACTIVE_FACETS_DESKTOP,
    );

    // Count active filter values
    activeFacets.forEach((facet) => {
      // Don't count the "Clear All" button
      if (!facet.querySelector(SELECTORS.ACTIVE_FACETS_BUTTON)) {
        count++;
      }
    });

    return count;
  }

  static updateBadgeCount() {
    const count = this.calculateFilterCount();
    const badges = document.querySelectorAll(
      `.mobile-facets__badge:not(${SELECTORS.SECTION_BADGE}), .facets-sticky-badge`,
    );

    badges.forEach((badge) => {
      badge.textContent = count;
      badge.classList.toggle("hidden", count === 0);
      badge.setAttribute("data-filter-count", count);
    });
  }

  static updateSectionBadges() {
    const mobileFilterDetails = document.querySelectorAll(
      SELECTORS.MOBILE_FILTER_DETAILS,
    );

    mobileFilterDetails.forEach((filterDetail) => {
      const summary = filterDetail.querySelector(SELECTORS.MOBILE_SUMMARY);
      if (!summary) return;

      // Check if it's a price range filter
      const priceRange = filterDetail.querySelector(SELECTORS.PRICE_RANGE);
      const activeCount = priceRange
        ? this.getPriceRangeFilterCount(priceRange)
        : this.getCheckboxFilterCount(filterDetail);

      this.updateFilterBadge(summary, activeCount);
    });
  }

  static getCheckboxFilterCount(filterDetail) {
    const checkboxes = filterDetail.querySelectorAll(
      `${SELECTORS.MOBILE_CHECKBOX}:checked`,
    );
    return checkboxes.length;
  }

  static getPriceRangeFilterCount(priceRange) {
    const inputs = priceRange.querySelectorAll("input[type='number']");
    const minInput = inputs[0];
    const maxInput = inputs[1];

    const hasMinValue =
      minInput &&
      minInput.value !== "" &&
      minInput.value !== null &&
      parseFloat(minInput.value) > 0;
    const hasMaxValue =
      maxInput &&
      maxInput.value !== "" &&
      maxInput.value !== null &&
      parseFloat(maxInput.value) > 0;

    return hasMinValue || hasMaxValue ? 1 : 0;
  }

  static updateFilterBadge(summary, count) {
    let badge = summary.querySelector(SELECTORS.SECTION_BADGE);
    const labelSpan = summary.querySelector("span:first-child");

    if (count > 0) {
      if (!badge) {
        badge = this.createSectionBadge(count);
        this.insertBadge(badge, labelSpan);
      } else {
        badge.textContent = count;
        badge.setAttribute("data-filter-count", count);
        badge.classList.remove("hidden");
      }
    } else {
      if (badge) {
        badge.remove();
      }
    }
  }

  static createSectionBadge(count) {
    const badge = document.createElement("span");
    badge.className = "mobile-facets__badge mobile-facets__badge--section";
    badge.setAttribute("data-filter-count", count);
    badge.textContent = count;
    return badge;
  }

  static insertBadge(badge, labelSpan) {
    if (!labelSpan) return;

    if (labelSpan.nextSibling) {
      labelSpan.parentNode.insertBefore(badge, labelSpan.nextSibling);
    } else {
      labelSpan.parentNode.appendChild(badge);
    }
  }

  static formatSortText(sortOptionName) {
    if (!sortOptionName) return "";

    // If contains comma, return second part
    if (sortOptionName.includes(",")) {
      const parts = sortOptionName.split(",");
      return parts[1] ? parts[1].trim() : sortOptionName;
    }

    return sortOptionName;
  }

  static updateSortText() {
    const sortSelects = document.querySelectorAll(SELECTORS.SORT_SELECTS);
    const sortTextElements = document.querySelectorAll(
      SELECTORS.SORT_TEXT_ELEMENTS,
    );

    // Get the current sort value from URL or first available select
    let currentSortValue = null;
    let currentSortText = "";

    sortSelects.forEach((select) => {
      if (select.selectedIndex >= 0) {
        const selectedOption = select.options[select.selectedIndex];
        if (selectedOption) {
          currentSortValue = select.value;
          currentSortText = selectedOption.text;
        }
      }
    });

    // If no select found, try to get from URL params
    if (!currentSortValue) {
      const urlParams = new URLSearchParams(window.location.search);
      currentSortValue = urlParams.get("sort_by");

      // Find matching option text
      sortSelects.forEach((select) => {
        for (let option of select.options) {
          if (option.value === currentSortValue) {
            currentSortText = option.text;
            break;
          }
        }
      });
    }

    if (currentSortText) {
      const formattedText = this.formatSortText(currentSortText);
      sortTextElements.forEach((element) => {
        element.textContent = formattedText;
        if (currentSortValue) {
          element.setAttribute("data-sort-value", currentSortValue);
        }
      });
    }
  }

  static setupSortChangeListener() {
    // Track which selects already have listeners to avoid duplicates
    const selectListeners = new WeakMap();

    const setupListeners = () => {
      const sortSelects = document.querySelectorAll(SELECTORS.SORT_SELECTS);

      sortSelects.forEach((select) => {
        // Only add listener if not already added
        if (!selectListeners.has(select)) {
          select.addEventListener("change", () => {
            this.updateSortText();
          });
          selectListeners.set(select, true);
        }
      });
    };

    // Setup initially
    setupListeners();

    // Store setup function to be called after AJAX updates
    this._setupSortListeners = setupListeners;
  }

  // Helper: Save and restore scroll position
  static saveScrollPosition() {
    return window.scrollY || window.pageYOffset;
  }

  static restoreScrollPosition(savedPosition) {
    setTimeout(() => {
      if (window.scrollY !== savedPosition) {
        window.scrollTo({
          top: savedPosition,
          behavior: "instant",
        });
      }
    }, TIMEOUT_DELAYS.SCROLL_RESTORE);
  }

  // Helper: Close drawer by clicking summary
  static closeDrawer(drawer) {
    if (!drawer) return;
    const summary = drawer.querySelector("summary");
    if (summary) {
      summary.click();
    }
  }

  static setupButtonPrevention() {
    // Track which elements already have listeners
    const buttonListeners = new WeakSet();

    // Prevent scroll on Filter & Sort button clicks
    const preventScrollOnClick = (element) => {
      if (buttonListeners.has(element)) return;
      buttonListeners.add(element);

      element.addEventListener(
        "click",
        (event) => {
          const scrollY = this.saveScrollPosition();
          this.restoreScrollPosition(scrollY);
        },
        { passive: true },
      );
    };

    // Setup for Filter & Sort buttons
    const filterButtons = document.querySelectorAll(
      ".mobile-facets__open-wrapper summary, .facets-sticky-button summary",
    );
    filterButtons.forEach(preventScrollOnClick);

    // Setup for drawer action buttons
    const actionListeners = new WeakSet();
    const setupDrawerPrevention = () => {
      const drawerActions = document.querySelectorAll(
        ".mobile-facets__footer .button--primary, .mobile-facets__close",
      );

      drawerActions.forEach((action) => {
        if (actionListeners.has(action)) return;
        actionListeners.add(action);

        action.addEventListener("click", (event) => {
          event.preventDefault();
          const scrollY = FacetStickyManager.saveScrollPosition();

          const drawer = action.closest(".mobile-facets__wrapper");
          if (
            action.classList.contains("button--primary") ||
            action.classList.contains("mobile-facets__close")
          ) {
            FacetStickyManager.closeDrawer(drawer);
          }

          FacetStickyManager.restoreScrollPosition(scrollY);
        });
      });
    };

    setupDrawerPrevention();

    // Store setup function to be called after AJAX updates
    this._setupDrawerPrevention = setupDrawerPrevention;
  }

  // Helper: Check if desktop viewport
  static isDesktop() {
    return window.innerWidth >= DESKTOP_BREAKPOINT;
  }

  // Helper: Get cached drawer elements
  static getDrawerElements() {
    if (!this._drawerElements) {
      this._drawerElements = {
        nonSticky: document.querySelector(
          ".facets-mobile-non-sticky .mobile-facets__wrapper",
        ),
        sticky: document.querySelector(".facets-sticky-drawer"),
      };
    }
    return this._drawerElements;
  }

  static handleDesktopDrawer() {
    // On desktop, prevent non-sticky drawer from opening
    const drawers = this.getDrawerElements();

    if (this.isDesktop() && drawers.nonSticky) {
      const summary = drawers.nonSticky.querySelector("summary");
      if (summary) {
        // Prevent click on desktop
        summary.addEventListener("click", (event) => {
          if (this.isDesktop()) {
            event.preventDefault();
            event.stopPropagation();
            return false;
          }
        });
      }
    }
  }

  static setupDesktopDrawerPrevention() {
    // Prevent non-sticky drawer from opening on desktop
    const checkAndPrevent = () => {
      const drawers = this.getDrawerElements();
      if (drawers.nonSticky && this.isDesktop()) {
        const details = drawers.nonSticky.querySelector("details");
        if (details && details.hasAttribute("open")) {
          details.removeAttribute("open");
        }
      }
    };

    window.addEventListener("resize", checkAndPrevent);
    checkAndPrevent();
  }

  static setupStickyDrawerActiveClass() {
    // Prevent multiple initializations
    if (this._stickyDrawerActiveSetup) return;
    this._stickyDrawerActiveSetup = true;

    const stickyWrapper = document.getElementById("FacetsStickyWrapper");
    const drawers = this.getDrawerElements();

    if (!stickyWrapper || !drawers.sticky) return;

    const detailsElement = drawers.sticky.querySelector("details");
    if (!detailsElement) return;

    // Function to update active class based on drawer state
    const updateActiveClass = () => {
      const isOpen = detailsElement.hasAttribute("open");
      const isVisible = stickyWrapper.classList.contains("visible");

      if (isOpen && isVisible) {
        stickyWrapper.classList.add("active");
      } else {
        stickyWrapper.classList.remove("active");
      }
    };

    detailsElement.addEventListener("toggle", updateActiveClass);

    // Hook into scroll detection to update when visibility changes
    // Store update function to be called from scroll detection
    this._updateStickyActiveClass = updateActiveClass;

    // Initial check
    updateActiveClass();
  }

  static setupDrawerBodyClass() {
    const BODY_CLASS = "facet-drawer-open";

    const updateBodyClass = () => {
      const drawers = this.getDrawerElements();
      const nonStickyDetails = drawers.nonSticky?.querySelector("details");
      const stickyDetails = drawers.sticky?.querySelector("details");

      const isNonStickyOpen = nonStickyDetails?.hasAttribute("open") || false;
      const isStickyOpen = stickyDetails?.hasAttribute("open") || false;

      if (isNonStickyOpen || isStickyOpen) {
        document.body.classList.add(BODY_CLASS);
      } else {
        document.body.classList.remove(BODY_CLASS);
      }
    };

    // Setup listeners for both drawers
    const setupDrawerListeners = () => {
      const drawers = this.getDrawerElements();

      const setupDrawerListener = (drawer) => {
        if (!drawer) return;
        const details = drawer.querySelector("details");
        if (details) {
          // Remove existing listener if any (to avoid duplicates)
          details.removeEventListener("toggle", updateBodyClass);
          details.addEventListener("toggle", updateBodyClass);
        }
      };

      setupDrawerListener(drawers.nonSticky);
      setupDrawerListener(drawers.sticky);
    };

    // Initial setup
    if (!this._drawerBodyClassSetup) {
      this._drawerBodyClassSetup = true;
      setupDrawerListeners();
    }

    // Store functions to be called after AJAX updates
    this._updateDrawerBodyClass = updateBodyClass;
    this._setupDrawerBodyClassListeners = setupDrawerListeners;

    // Initial check
    updateBodyClass();
  }
}

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    FacetStickyManager.init();
  });
} else {
  FacetStickyManager.init();
}

// Re-initialize after AJAX updates
const originalRenderPage = FacetFiltersForm.renderPage;
FacetFiltersForm.renderPage = function (searchParams, event, updateURLHash) {
  originalRenderPage.call(this, searchParams, event, updateURLHash);
  // Update sticky elements after page render - debounce to avoid loops
  if (FacetFiltersForm._updateTimeout) {
    clearTimeout(FacetFiltersForm._updateTimeout);
  }
  FacetFiltersForm._updateTimeout = setTimeout(() => {
    FacetStickyManager.updateBadgeCount();
    FacetStickyManager.updateSectionBadges();
    FacetStickyManager.updateSortText();
    FacetStickyManager.handleDesktopDrawer();
    // Re-setup listeners after AJAX updates
    if (FacetStickyManager._setupSortListeners) {
      FacetStickyManager._setupSortListeners();
    }
    if (FacetStickyManager._setupDrawerPrevention) {
      FacetStickyManager._setupDrawerPrevention();
    }
    // Re-setup drawer body class after DOM updates
    if (FacetStickyManager._setupDrawerBodyClassListeners) {
      FacetStickyManager._setupDrawerBodyClassListeners();
    }
    if (FacetStickyManager._updateDrawerBodyClass) {
      FacetStickyManager._updateDrawerBodyClass();
    }
    // Don't re-setup scroll detection - it's already set up once
  }, TIMEOUT_DELAYS.AJAX_UPDATE);
};
